import { selectCurrentHotelName } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import FolderEmpty from 'components/ReusableComponents/Icons/FolderEmpty'
import FolderFilled from 'components/ReusableComponents/Icons/FolderFilled'
import { ReactComponent as ExcelIcon } from 'components/ReusableComponents/Icons/svg/excel.svg'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import exportRateCodesToExcel from 'features/RateCodes/exportToExcel'
import {
  createUnknownRowsCsvDownloadUrl,
  mergeImportedRateCodes,
  parseRateCodeWorkbook,
  rateCodesMatch,
  summarizeRowsByCategory
} from 'features/RateCodes/importFromExcel'
import { hotelNamesLookRelated } from 'features/RateCodes/lighthouseFormat'
import {
  rateCodeCategoryNames,
  rateCodeCategoryValues
} from 'features/RateCodes/rateCodeCategories'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import { useEffect, useMemo, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import subsidiariesApi from 'services/api/subsidiaries'

import { ReactComponent as DragIcon } from './drag.svg'
import { ReactComponent as NoConversionIcon } from './noConvert.svg'
import { ReactComponent as NoReachIcon } from './noReach.svg'
// RateCodeImporter.jsx
import styles from './RateCodes.module.scss'

async function getMergedRateCodes(isIndependent) {
  return new Promise((resolve, reject) => {
    if (isIndependent) {
      subsidiariesApi.rateCodes
        .get({ isSubsidiaryLevel: true })
        .then(res => resolve(res))
        .catch(err => reject(err))
      return
    }

    subsidiariesApi.rateCodes
      .get({ isSubsidiaryLevel: false })
      .then(brandLevelRates => {
        subsidiariesApi.rateCodes
          .get({ isSubsidiaryLevel: true })
          .then(hotelLevelRates => {
            const mergedRates = { ...hotelLevelRates }

            Object.keys(brandLevelRates).forEach(category => {
              if (category !== 'Unknown') {
                // For other categories, remove rates from Unknown in hotel level if they exist in brand level category
                const brandLevelRatesToAdd = brandLevelRates[category].map(rate => ({
                  ...rate,
                  fromBrandLevel: true
                }))

                mergedRates[category] = [
                  ...(mergedRates[category] || []),
                  ...brandLevelRatesToAdd
                ]

                // Remove these rates from Unknown in hotel level
                if (mergedRates.Unknown) {
                  mergedRates.Unknown = mergedRates.Unknown.filter(
                    rate =>
                      !brandLevelRates[category].some(r => rateCodesMatch(r.code, rate.code))
                  )
                }
              }
            })
            resolve(mergedRates)
          })
          .catch(err => reject(err))
      })
      .catch(err => reject(err))
  })
}

export { rateCodeCategoryNames, rateCodeCategoryValues }

export function filterRatesBySearch(rates, filterValue, filterType) {
  if (!rates?.length) {
    return rates ? [...rates] : []
  }
  if (!filterValue) {
    return [...rates]
  }
  const q = filterValue.toLowerCase()
  return rates.filter(rate => {
    if (filterType === 'Starts with') {
      return rate.code.toLowerCase().startsWith(q)
    }
    if (filterType === 'Contains') {
      return rate.code.toLowerCase().includes(q)
    }
    return true
  })
}

const Category = ({
  id,
  codes,
  isCollapsedByDefault,
  isCollapsable,
  isFiltered,
  isNoConvert = false,
  isNoReach = false
}) => {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedByDefault)
  if (id === 'Unknown' && codes?.length > 0 && isFiltered) {
    return (
      <Droppable droppableId={id}>
        {droppableProvided => (
          <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
            <Draggable draggableId="all-filtered" index={-1}>
              {(provided, snapshot) => (
                <div className={styles.category}>
                  <div
                    className={styles.categoryTitle}
                    onClick={() => (isCollapsable ? setIsCollapsed(c => !c) : null)}
                    style={{ cursor: isCollapsable ? 'pointer' : 'default' }}
                  >
                    {id === 'Unknown' ? (
                      <FolderEmpty />
                    ) : (
                      <FolderFilled
                        fill={id === 'DirectfulCode' ? 'rgb(155, 81, 224)' : '#A8ADB2'}
                      />
                    )}{' '}
                    {rateCodeCategoryNames[id]} ({codes && codes.length})
                  </div>
                  {!isCollapsed && (
                    <>
                      <div {...provided.draggableProps}>
                        <div
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          className={styles.allFilteredHandle}
                        >
                          <DragIcon
                            style={{
                              width: '16px',
                              height: '16px',
                              verticalAlign: 'middle',
                              marginRight: '8px',
                              marginBottom: '2px',
                              fill: snapshot.isDragging ? 'var(--black)' : 'var(--gray)'
                            }}
                          />
                          Drag all filtered
                        </div>
                        <div
                          className={
                            codes && codes.length > 0
                              ? styles.categoryInner
                              : styles.categoryInnerEmpty
                          }
                          style={{
                            borderColor:
                              id === 'Directful' ? 'rgba(155, 81, 224, 0.4)' : null,
                            borderWidth: id === 'Directful' ? '2px' : null
                          }}
                        >
                          {codes?.length > 0 &&
                            codes.map((item, index) => (
                              <RateCard key={item.code} item={item} index={index} />
                            ))}
                          {droppableProvided.placeholder}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Draggable>
          </div>
        )}
      </Droppable>
    )
  }

  return (
    <Droppable droppableId={id}>
      {provided => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={styles.category}
        >
          <div
            className={styles.categoryTitle}
            onClick={() => (isCollapsable ? setIsCollapsed(c => !c) : null)}
            style={{ cursor: isCollapsable ? 'pointer' : 'default' }}
          >
            {id === 'Unknown' ? (
              <FolderEmpty />
            ) : (
              <FolderFilled
                fill={id === 'DirectfulCode' ? 'rgb(155, 81, 224)' : '#A8ADB2'}
              />
            )}{' '}
            {rateCodeCategoryNames[id]}
            {isCollapsable && (
              <button className={styles.collapseButton}>
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 10l-1.41-1.41L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                    fill="#A8ADB2"
                    style={{
                      transform: isCollapsed ? 'rotate(0)' : 'rotate(180deg)',
                      transformOrigin: 'center'
                    }}
                  />
                </svg>
              </button>
            )}
            {id === 'Unknown' && ` (${codes && codes.length})`}
            <div className={styles.exclusionIcons}>
              {isNoReach && <NoReachIcon />}
              {isNoConvert && <NoConversionIcon />}
            </div>
          </div>
          {!isCollapsed && (
            <>
              <div
                className={
                  codes && codes.length > 0
                    ? styles.categoryInner
                    : styles.categoryInnerEmpty
                }
                style={{
                  borderColor: id === 'Directful' ? 'rgba(155, 81, 224, 0.4)' : null,
                  borderWidth: id === 'Directful' ? '2px' : null
                }}
              >
                {id === 'Unknown' && isFiltered && codes?.length > 0 && (
                  <AllItemsCard count={codes ? codes.length : 0} />
                )}
                {codes?.length > 0 &&
                  codes.map((item, index) => (
                    <RateCard key={item.code} item={item} index={index} />
                  ))}
                {provided.placeholder}
              </div>
            </>
          )}
        </div>
      )}
    </Droppable>
  )
}

function getRateCardBackgroundColor(item) {
  if (item.fromBrandLevel) {
    return '#faf4ff'
  }
  if (item.isImported) {
    return '#DCFCE7'
  }
  return 'white'
}

const RateCard = ({ item, index }) => (
  <Draggable
    key={item.code}
    draggableId={item.code}
    index={index}
    isDragDisabled={item.fromBrandLevel === true}
  >
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={snapshot.isDragging ? styles.rateCardDragging : styles.rateCard}
        style={{
          ...provided.draggableProps.style,
          backgroundColor: getRateCardBackgroundColor(item),
          cursor: item.fromBrandLevel ? 'not-allowed' : 'grab'
        }}
      >
        <span className={styles.rateCode}>{item.code}</span>

        {item.isImported && <span className={styles.bookingsCount}>Imported</span>}

        {item.fromBrandLevel && (
          <span
            className={styles.bookingsCount}
            style={{ fontStyle: 'italic', opacity: 0.7 }}
          >
            Inherited from the brand
          </span>
        )}

        {!item.fromBrandLevel && !item.isImported && (
          <span className={styles.bookingsCount}>
            {item.bookingsCount} booking{item.bookingsCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
    )}
  </Draggable>
)

const AllItemsCard = ({ count }) => (
  <Draggable key="all-filtered" draggableId="all-filtered" index={-1}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={snapshot.isDragging ? styles.rateCardDragging : styles.rateCard}
      >
        <span className={styles.allRateCodes}>All filtered</span>
        <span className={styles.bookingsCount}>{count} codes</span>
      </div>
    )}
  </Draggable>
)

const importFormatNames = {
  native: 'Directful rate codes export',
  lighthouse: 'Lighthouse market segment mapping'
}

const downloadLinkStyle = backgroundColor => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  backgroundColor,
  color: 'white',
  textDecoration: 'none',
  borderRadius: '6px',
  fontWeight: '500'
})

function revokeDownloadUrls(downloadUrls) {
  Object.values(downloadUrls).forEach(url => {
    if (url) {
      URL.revokeObjectURL(url)
    }
  })
}

const RateCodeImporter = ({ hotelName, onImport }) => {
  const { addToast } = useToasts()
  const [importResult, setImportResult] = useState(null)
  const [downloadUrls, setDownloadUrls] = useState({ review: null, unknown: null })

  const handleUpload = async e => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset state
    setDownloadUrls({ review: null, unknown: null })
    setImportResult(null)

    const result = await parseRateCodeWorkbook(file)

    if (!result.valid) {
      result.errors.forEach(error => {
        addToast(error, { appearance: 'error', autoDismiss: false })
      })
      e.target.value = ''
      return
    }

    // Show warnings if any
    result.warnings.forEach(warning => {
      addToast(warning, { appearance: 'warning', autoDismiss: true })
    })

    if (!hotelNamesLookRelated(result.hotelNameFromFile, hotelName)) {
      addToast(
        `This file looks like it belongs to "${result.hotelNameFromFile}" but "${hotelName}" is selected. Check you are importing into the right hotel.`,
        { appearance: 'warning', autoDismiss: false }
      )
    }

    const nonEmptyCategories = Object.keys(result.buckets).reduce((acc, key) => {
      if (result.buckets[key].length > 0) {
        acc[key] = result.buckets[key]
      }
      return acc
    }, {})

    // Calculate total imported count
    const totalImported = Object.values(nonEmptyCategories).reduce(
      (sum, arr) => sum + arr.length,
      0
    )

    setDownloadUrls({
      review:
        result.uncategorizedRows.length > 0
          ? createUnknownRowsCsvDownloadUrl(result.uncategorizedRows)
          : null,
      unknown:
        result.unknownRows.length > 0
          ? createUnknownRowsCsvDownloadUrl(result.unknownRows)
          : null
    })

    setImportResult({
      format: result.format,
      importedCount: totalImported,
      reviewCount: result.uncategorizedRows.length,
      reviewSummary: summarizeRowsByCategory(result.uncategorizedRows),
      unknownCount: result.unknownRows.length
    })

    // Only import if there are valid rows
    if (Object.keys(nonEmptyCategories).length > 0) {
      onImport(nonEmptyCategories)
    } else {
      addToast('No rate codes could be categorized from this file.', {
        appearance: 'warning',
        autoDismiss: false
      })
    }

    e.target.value = '' // allow re-upload same file
  }

  // Cleanup blob URLs on unmount and whenever a new file replaces them
  useEffect(() => {
    return () => {
      revokeDownloadUrls(downloadUrls)
    }
  }, [downloadUrls])

  return (
    <div style={{ padding: '20px 0' }}>
      <input type="file" accept=".xlsx" onChange={handleUpload} />

      {importResult && (
        <p style={{ margin: '12px 0 0 0', color: 'var(--gray)' }}>
          Detected format: {importFormatNames[importResult.format]}
        </p>
      )}

      {importResult && importResult.importedCount > 0 && (
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#DCFCE7',
            borderRadius: '8px',
            border: '1px solid #22C55E'
          }}
        >
          <p style={{ margin: '0', color: '#166534' }}>
            <strong>{importResult.importedCount}</strong> rate code(s) categorized. Use
            Save &amp; close to store them.
          </p>
        </div>
      )}

      {importResult && importResult.reviewCount > 0 && (
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#FEF3C7',
            borderRadius: '8px',
            border: '1px solid #F59E0B'
          }}
        >
          <p style={{ margin: '0 0 12px 0', color: '#92400E' }}>
            <strong>{importResult.reviewCount}</strong> row(s) stay uncategorized because
            the file does not say which of our categories applies. Categorize them by hand:
          </p>
          <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px', color: '#92400E' }}>
            {importResult.reviewSummary.map(entry => (
              <li key={entry.category}>
                {entry.count} {entry.category}
              </li>
            ))}
          </ul>
          {downloadUrls.review && (
            <a
              href={downloadUrls.review}
              download="rate-codes-to-review.csv"
              style={downloadLinkStyle('#F59E0B')}
            >
              Download Rows To Review (CSV)
            </a>
          )}
        </div>
      )}

      {importResult && importResult.unknownCount > 0 && (
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#FEE2E2',
            borderRadius: '8px',
            border: '1px solid #EF4444'
          }}
        >
          <p style={{ margin: '0 0 12px 0', color: '#991B1B' }}>
            <strong>{importResult.unknownCount}</strong> row(s) used a category value we do
            not recognize and were skipped.
          </p>
          {downloadUrls.unknown && (
            <a
              href={downloadUrls.unknown}
              download="unknown-rate-codes.csv"
              style={downloadLinkStyle('#EF4444')}
            >
              Download Unknown Rows (CSV)
            </a>
          )}
        </div>
      )}
    </div>
  )
}

const RateCodes = () => {
  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)
  const shouldDisplayParentChainTab = parentChainCode !== 'independent'

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [tab, setTab] = useState('hotelLevel')
  const [showUnchangesWarningDialog, setShowUnchangesWarningDialog] = useState(false)

  const [rateState, setRateState] = useState(null)

  const [changes, setChanges] = useState([])

  const [isImporterVisible, setIsImporterVisible] = useState(false)

  const currentHotelName = useSelector(selectCurrentHotelName)

  const [filterValue, setFilterValue] = useState('')
  const [filterType, setFilterType] = useState('Starts with')

  const displayId = useSelector(s => s.hotelDetails.displayId)

  const unknownRates = useMemo(() => {
    if (rateState === null) {
      return []
    }
    const unknown = rateState.Unknown || []
    return filterRatesBySearch(unknown, filterValue, filterType)
  }, [rateState, filterValue, filterType])

  const getDisplayedRatesForDroppable = droppableId => {
    if (rateState === null) {
      return []
    }
    if (droppableId === 'Unknown') {
      return unknownRates
    }
    return filterRatesBySearch(
      rateState[droppableId] || [],
      filterValue,
      filterType
    )
  }

  const history = useHistory()

  useEffect(() => {
    setIsLoading(true)

    if (tab === 'hotelLevel') {
      getMergedRateCodes(parentChainCode === 'independent')
        .then(res => {
          setRateState(res)
          setIsErred(false)
        })
        .catch(() => {
          setIsErred(true)
        })
        .finally(() => {
          setIsLoading(false)
        })
      return
    }

    subsidiariesApi.rateCodes
      .get({ isSubsidiaryLevel: tab === 'hotelLevel' })
      .then(res => {
        setRateState(res)
        setIsErred(false)
      })
      .catch(() => {
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [tab])

  function onSave() {
    if (changes.length === 0) {
      return
    }

    setIsLoading(true)

    subsidiariesApi.rateCodes
      .put({ rateCodes: changes })
      .then(() => {
        history.push('/hotelDetails')
      })
      .catch(() => {
        setIsErred(true)
      })
  }

  const handleImportedRateCodes = importedRateCodes => {
    const { updatedRateState, newChanges } = mergeImportedRateCodes(
      rateState,
      importedRateCodes,
      tab === 'hotelLevel'
    )

    setChanges(c => {
      const mergedChanges = [...c]

      newChanges.forEach(change => {
        const existingChangeIndex = mergedChanges.findIndex(existingChange =>
          rateCodesMatch(existingChange.code, change.code)
        )

        if (existingChangeIndex !== -1) {
          mergedChanges[existingChangeIndex] = change
        } else {
          mergedChanges.push(change)
        }
      })

      return mergedChanges
    })
    setRateState(updatedRateState)
    //setIsImporterVisible(false)
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const droppableId = source.droppableId
      const full = [...(rateState[droppableId] || [])]

      if (filterValue === '') {
        const [removed] = full.splice(source.index, 1)
        full.splice(destination.index, 0, removed)

        setRateState({
          ...rateState,
          [droppableId]: full
        })
      } else {
        const displayed = filterRatesBySearch(full, filterValue, filterType)
        const reordered = [...displayed]
        const [removed] = reordered.splice(source.index, 1)
        reordered.splice(destination.index, 0, removed)
        const displayedSet = new Set(displayed.map(r => r.code))
        let ri = 0
        const newFull = []
        for (const item of full) {
          if (displayedSet.has(item.code)) {
            if (ri < reordered.length) {
              newFull.push(reordered[ri++])
            }
          } else {
            newFull.push(item)
          }
        }
        setRateState({
          ...rateState,
          [droppableId]: newFull
        })
      }
      return
    }

    if (source.droppableId !== destination.droppableId) {
      const allSourceItems = rateState[source.droppableId] || []
      const sourceItems = getDisplayedRatesForDroppable(source.droppableId)

      if (source.index === -1) {
        const updatedDestItems = [...rateState[destination.droppableId], ...unknownRates]

        const updatedSourceItems = allSourceItems.filter(
          i => unknownRates.find(u => u.code === i.code) === undefined
        )

        setChanges(c => [
          ...c.filter(prev => !unknownRates.find(r => r.code === prev.code)),
          ...unknownRates.map(r => ({
            isSubsidiaryLevel: tab === 'hotelLevel',
            code: r.code,
            kind: destination.droppableId
          }))
        ])

        setRateState({
          ...rateState,
          [source.droppableId]: updatedSourceItems,
          [destination.droppableId]: updatedDestItems
        })
        return
      }

      const targetItem = sourceItems[source.index]
      if (!targetItem) {
        return
      }

      const destItems = [...rateState[destination.droppableId]]

      // remove the targetItem from allSourceItems

      const updatedSourceItems = allSourceItems.filter(i => i.code !== targetItem.code)

      setChanges(c => [
        ...c.filter(prev => prev.code !== targetItem.code),
        {
          isSubsidiaryLevel: tab === 'hotelLevel',
          code: targetItem.code,
          kind: destination.droppableId
        }
      ])

      const updatedDestItems = [...destItems, targetItem]

      setRateState({
        ...rateState,
        [source.droppableId]: updatedSourceItems,
        [destination.droppableId]: updatedDestItems
      })
    }
  }

  const handleTabChange = tab => {
    if (changes.length > 0) {
      setShowUnchangesWarningDialog(true)
    } else {
      setTab(tab)
    }
  }

  const handleUnsavedChangesSubmit = () => {
    setChanges([])
    setShowUnchangesWarningDialog(false)
    setTab(prev =>
      prev === 'hotelLevel' ? parentChainNames[parentChainCode] : 'hotelLevel'
    )
  }

  if (isImporterVisible) {
    return (
      <ConfirmationDialog
        onClose={() => setIsImporterVisible(false)}
        hasConfirmButton={false}
        title="Import rate codes from Excel"
      >
        <RateCodeImporter
          hotelName={currentHotelName}
          onImport={handleImportedRateCodes}
        />
      </ConfirmationDialog>
    )
  }

  return (
    <>
      <ConfirmationDialog
        maxWidth={1000}
        title={`Rate codes categorization of ${currentHotelName}`}
        theme="blacklist"
        positiveMessage="Save & close"
        negativeMessage="Cancel"
        contentPadding="0"
        onReject={() => history.push('/hotelDetails')}
        onConfirm={onSave}
      >
        {isErred && <div className={styles.error}>Something went wrong</div>}
        {!isErred && (
          <div className={styles.wrapper}>
            {isLoading ? (
              <LoadingIndicator />
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <div className={styles.filterContainer}>
                  <div className={styles.tabs}>
                    <button
                      className={
                        tab === 'hotelLevel' ? styles.activeTab : styles.emptyTab
                      }
                      onClick={() =>
                        shouldDisplayParentChainTab ? handleTabChange('hotelLevel') : null
                      }
                    >
                      Hotel level
                    </button>
                    {shouldDisplayParentChainTab && (
                      <button
                        className={
                          tab !== 'hotelLevel' ? styles.activeTab : styles.emptyTab
                        }
                        onClick={() => handleTabChange(parentChainNames[parentChainCode])}
                      >
                        {`Brand level`}
                      </button>
                    )}
                    <div className={styles.tabIcons}>
                      <div className={styles.tabIcon}>
                        <NoReachIcon />
                        <span>Will not reached</span>
                      </div>
                      <div className={styles.tabIcon}>
                        <NoConversionIcon />
                        <span>Will not convert</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.filterContainer}>
                  <TextInput
                    placeholder="Search rate codes"
                    className={styles.search}
                    value={filterValue}
                    onChange={e => setFilterValue(e.target.value)}
                    type="search"
                  />
                  <Dropdown
                    options={{
                      'Starts with': 'Starts with',
                      Contains: 'Contains'
                    }}
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className={styles.dropdown}
                  />
                  <div
                    style={{
                      height: 40,
                      borderLeft: '1px solid #ccc'
                    }}
                  />
                  <PrimaryRegularButton
                    className={styles.exportButton}
                    onClick={() =>
                      exportRateCodesToExcel({
                        buckets: { Unknown: rateState.Unknown },
                        fileName: `rate-codes-uncategorized-${displayId}.xlsx`
                      })
                    }
                  >
                    <ExcelIcon
                      style={{
                        width: '24px',
                        height: '24px',
                        verticalAlign: 'middle',
                        marginRight: '8px',
                        marginLeft: '-4px',
                        fill: 'white'
                      }}
                    />
                    Export
                  </PrimaryRegularButton>
                  <PrimaryRegularButton
                    className={styles.exportButton}
                    onClick={() => setIsImporterVisible(true)}
                  >
                    <ExcelIcon
                      style={{
                        width: '24px',
                        height: '24px',
                        verticalAlign: 'middle',
                        marginRight: '8px',
                        marginLeft: '-4px',
                        fill: 'white'
                      }}
                    />
                    Import
                  </PrimaryRegularButton>
                </div>
                <div className={styles.columns}>
                  <div className={styles.column}>
                    <Category
                      id="DirectfulCode"
                      codes={filterRatesBySearch(
                        rateState.DirectfulCode,
                        filterValue,
                        filterType
                      )}
                    />
                    <Category
                      id="OTA"
                      codes={filterRatesBySearch(
                        rateState.OTA,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                    />
                    <Category
                      id="OTAOpaque"
                      codes={filterRatesBySearch(
                        rateState.OTAOpaque,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isNoConvert
                      isCollapsedByDefault={rateState.OTAOpaque.length > 0}
                    />
                    <Category
                      id="Employee"
                      codes={filterRatesBySearch(
                        rateState.Employee,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.Employee.length > 0}
                    />
                    <Category
                      id="FriendsAndFamily"
                      codes={filterRatesBySearch(
                        rateState.FriendsAndFamily,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.FriendsAndFamily.length > 0}
                    />
                    <Category
                      id="LongTermResident"
                      codes={filterRatesBySearch(
                        rateState.LongTermResident,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.LongTermResident.length > 0}
                    />
                    <Category
                      id="AirlineDistress"
                      codes={filterRatesBySearch(
                        rateState.AirlineDistress,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isNoReach
                      isNoConvert
                      isCollapsedByDefault={rateState.AirlineDistress.length > 0}
                    />
                    <Category
                      id="GroupNoConvert"
                      codes={filterRatesBySearch(
                        rateState.GroupNoConvert,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isNoConvert
                      isCollapsedByDefault={rateState?.GroupNoConvert?.length > 0}
                    />
                    <Category
                      id="GroupNoReach"
                      codes={filterRatesBySearch(
                        rateState.GroupNoReach,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isNoReach
                      isCollapsedByDefault={rateState?.GroupNoReach?.length > 0}
                    />
                    <Category
                      id="Generic"
                      codes={filterRatesBySearch(
                        rateState.Generic,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault={rateState?.Generic?.length > 0}
                    />
                    <Category
                      id="CorporateLocal"
                      codes={filterRatesBySearch(
                        rateState.CorporateLocal,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="CorporateBrand"
                      codes={filterRatesBySearch(
                        rateState.CorporateBrand,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="GovernmentLocal"
                      codes={filterRatesBySearch(
                        rateState.GovernmentLocal,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="GovernmentBrand"
                      codes={filterRatesBySearch(
                        rateState.GovernmentBrand,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="Complimentary"
                      codes={filterRatesBySearch(
                        rateState.Complimentary,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="RewardPointsClaim"
                      codes={filterRatesBySearch(
                        rateState.RewardPointsClaim,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountLocal"
                      codes={filterRatesBySearch(
                        rateState.DiscountLocal,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountBrand"
                      codes={filterRatesBySearch(
                        rateState.DiscountBrand,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                    <Category
                      id="DiscountClub"
                      codes={filterRatesBySearch(
                        rateState.DiscountClub,
                        filterValue,
                        filterType
                      )}
                      isCollapsable
                      isCollapsedByDefault
                    />
                  </div>
                  <div className={styles.column}>
                    <Category
                      id="Unknown"
                      codes={unknownRates}
                      isFiltered={filterValue !== ''}
                    />
                  </div>
                </div>
              </DragDropContext>
            )}
          </div>
        )}
      </ConfirmationDialog>
      {showUnchangesWarningDialog && (
        <ConfirmationDialog
          title="Unsaved changes"
          positiveMessage="Discard"
          negativeMessage="Go back"
          theme="blacklist"
          onConfirm={handleUnsavedChangesSubmit}
          onClose={() => setShowUnchangesWarningDialog(false)}
          onPressEnter={handleUnsavedChangesSubmit}
        >
          <div>You've unsaved changes. Are you sure you want to leave?</div>
        </ConfirmationDialog>
      )}
    </>
  )
}

export default RateCodes
